import { useState } from 'react'
import { Hash, Volume2 } from 'lucide-react'
import { LIMITS } from '@nexus/shared'
import { api, ApiError } from '@/lib/api'
import { useAppStore } from '@/store/app'
import { Button, Field, Input, Modal, Textarea } from '@/components/ui'

export function CreateGuildModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const selectGuild = useAppStore((s) => s.selectGuild)
  const applyEvent = useAppStore((s) => s.applyEvent)

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [template, setTemplate] = useState<'default' | 'empty'>('default')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const reset = () => {
    setName('')
    setDescription('')
    setTemplate('default')
    setError(null)
  }

  const handleSubmit = async () => {
    const trimmed = name.trim()
    if (trimmed.length < LIMITS.guildName.min) {
      setError(`O nome precisa de ao menos ${LIMITS.guildName.min} caracteres`)
      return
    }

    setSaving(true)
    setError(null)

    try {
      const payload = await api.guilds.create({
        name: trimmed,
        description: description.trim() || undefined,
        template,
      })

      // O evento do gateway também chega, mas aplicar aqui evita o intervalo em
      // que o servidor já existe e a interface ainda não sabe.
      applyEvent({ op: 'guild_create', d: payload, s: 0 })
      selectGuild(payload.guild.id)

      reset()
      onClose()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível criar o servidor')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => {
        reset()
        onClose()
      }}
      title="Criar servidor"
      description="Um espaço para sua comunidade conversar, falar e compartilhar tela."
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={() => void handleSubmit()} loading={saving}>
            Criar servidor
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Nome do servidor" required error={error ?? undefined}>
          <Input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && void handleSubmit()}
            maxLength={LIMITS.guildName.max}
            placeholder="Time de produto"
          />
        </Field>

        <Field label="Descrição" hint="Opcional. Aparece na tela de convite.">
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={LIMITS.description.max}
            rows={2}
            placeholder="Do que se trata este servidor?"
          />
        </Field>

        <Field label="Estrutura inicial">
          <div className="grid gap-2 sm:grid-cols-2">
            <TemplateOption
              selected={template === 'default'}
              onClick={() => setTemplate('default')}
              title="Começar pronto"
              description="Cria #geral e uma sala de voz."
            />
            <TemplateOption
              selected={template === 'empty'}
              onClick={() => setTemplate('empty')}
              title="Começar vazio"
              description="Sem canais. Você monta do zero."
            />
          </div>
        </Field>
      </div>
    </Modal>
  )
}

function TemplateOption({
  selected,
  onClick,
  title,
  description,
}: {
  selected: boolean
  onClick: () => void
  title: string
  description: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-lg border p-3 text-left transition-colors ${
        selected
          ? 'border-brand bg-brand-subtle'
          : 'border-line bg-surface-base hover:border-line-strong'
      }`}
    >
      <div className="mb-1.5 flex gap-1.5 text-content-muted">
        <Hash className="size-4" />
        <Volume2 className="size-4" />
      </div>
      <p className="text-sm font-medium text-content-primary">{title}</p>
      <p className="mt-0.5 text-xs text-content-secondary">{description}</p>
    </button>
  )
}
